import {
  EmproveDomainStatus,
  EmproveSubscriptionType,
  EmproveUserData,
  BigDataQueryInput,
  EmproveDoc,
} from '@src/types/graphql-types'
import { LoggedInUserFragment } from '@src/queries/CurrentUserQuery.generated'
import messages from '@utils/messages'
import LockIcon from '@icons/LockIcon'
import CartIcon from '@icons/CartIcon'
import DownloadIcon from '@icons/DownloadIcon'

export enum Actions {
  Purchase = 'purchase',
  Download = 'download',
}

const documentAccessTypes = {
  // all have icons, some have messagese, some have disabled messages
  freeLocked: {
    Icon: LockIcon,
    message: messages.DOCUMENT_FREE_LOCKED,
  },
  locked: {
    Icon: LockIcon,
    message: messages.DOCUMENT_AVAILABLE,
  },
  freeDownloadable: {
    Icon: DownloadIcon,
    message: messages.DOCUMENT_FREE_DOWNLOADABLE,
    action: Actions.Download,
  },
  disabled: {
    Icon: LockIcon,
    disabled: true,
    message: messages.DOCUMENT_DISABLED,
  },
  downloadable: {
    Icon: DownloadIcon,
    action: Actions.Download,
  },
  purchasable: {
    Icon: CartIcon,
    message: messages.DOCUMENT_AVAILABLE,
    action: Actions.Purchase,
  },
  pending: {
    Icon: CartIcon,
    disabled: true,
  },
}

enum AccessType {
  FreeLocked = 'freeLocked',
  Locked = 'locked',
  FreeDownloadable = 'freeDownloadable',
  Purchasable = 'purchasable',
  Disabled = 'disabled',
  Pending = 'pending',
  Downloadable = 'downloadable',
}

const dossierIncludedInMembershipAndActive = (document, userData) => {
  const activeSubscriptionForDossier =
    userData &&
    userData?.dossierItemInfo &&
    userData?.dossierItemInfo.find(
      (item) =>
        item['subscriptionStatus'] === 'subscriber' &&
        item?.dossierId === document.assetId
    )

  return !!activeSubscriptionForDossier
}

const determineDocumentAccess = (
  document,
  userData,
  userIsLoggedIn,
  isPublicOrderingCustomer
) => {
  const edoDocument = 'EDO'
  const cedo5Document = 'CEDO5'
  // CEDO4 documents can only be downloaded by `domainStatus === approved` and `subscriptionType === all`
  const cedo4Document = 'CEDO4'
  const userStatus = userData?.domainStatus
  const subscriptionType = userData?.subscriptionType

  if (!userIsLoggedIn) {
    if (
      document.type === edoDocument ||
      document.type === cedo5Document ||
      document.type === cedo4Document
    )
      return AccessType.FreeLocked
    return AccessType.Locked
  } else {
    switch (userStatus) {
      case EmproveDomainStatus.New:
        // user is new -> can download free EDO doc & otherwise purchase docs
        if (
          document.type === edoDocument ||
          document.type === cedo5Document ||
          document.type === cedo4Document
        )
          return AccessType.FreeDownloadable
        if (!isPublicOrderingCustomer) return AccessType.Pending
        return AccessType.Purchasable

      case EmproveDomainStatus.Approved:
        // subscription type is single -> can download free EDO doc
        if (subscriptionType === EmproveSubscriptionType.Single) {
          if (
            document.type === edoDocument ||
            document.type === cedo5Document ||
            document.type === cedo4Document
          )
            return AccessType.FreeDownloadable
        }
        // subscription type is all -> can download all if subscription is active
        else if (subscriptionType === EmproveSubscriptionType.All) {
          if (userData.activeSubscription) return AccessType.Downloadable
          // inactive subscrition -> can download free EDO doc
          if (
            document.type === edoDocument ||
            document.type === cedo5Document ||
            document.type === cedo4Document
          )
            return AccessType.FreeDownloadable
        }
        // can download docs they already have access to, otherwise can purchase
        if (dossierIncludedInMembershipAndActive(document, userData))
          return AccessType.Downloadable
        if (
          document.type === edoDocument ||
          document.type === cedo5Document ||
          document.type === cedo4Document
        )
          return AccessType.FreeDownloadable
        if (!isPublicOrderingCustomer) return AccessType.Pending
        return AccessType.Purchasable

      default:
        // can download free EDO doc [if it's the only doc they can download, don't show message], all others blocked/pending
        if (
          document.type === edoDocument ||
          document.type === cedo5Document ||
          document.type === cedo4Document
        )
          return AccessType.Downloadable
        if (userStatus === EmproveDomainStatus.Pending)
          return AccessType.Pending
        if (userStatus === EmproveDomainStatus.Blocked)
          return AccessType.Disabled
        if (userStatus === 'expired') return AccessType.Disabled
    }
  }
  return null
}

export const documentAccessType = (
  document,
  userData,
  userIsLoggedIn,
  isPublicOrderingCustomer
) => {
  return documentAccessTypes[
    determineDocumentAccess(
      document,
      userData,
      userIsLoggedIn,
      isPublicOrderingCustomer
    ) || ''
  ]
}

export const getBigQueryData = (
  user: LoggedInUserFragment,
  dossierData: EmproveDoc,
  emprove: EmproveUserData,
  origin: string
): BigDataQueryInput => {
  const {
    id,
    metadata: { country, locale },
  } = user
  const { fileName, type, product, brand, productName } = dossierData
  const { domainId, companyName } = emprove

  return {
    user_id: id || '',
    country: country || '',
    language: locale ? locale.split('_')[0] : '',
    document_url: fileName ? 'deepweb/assets/emprove/' + fileName : '',
    document_type: type || '',
    product_number: product || '',
    brand: brand || '',
    product_name: productName || '',
    email_domain: domainId || '',
    company: companyName || '',
    origin,
  }
}
